<template>
  <b-modal
    id="subscription-form-container"
    :title="title"
    v-model="showModal"
    hide-footer
    centered
  >
    <form data-vv-scope="subscription-form" @submit.prevent="save">
      <div class="w-100 pl-5 pr-5 mb-32">
        <b-alert
          variant="info"
          show
          class="text-sm"
        >
          <h4 class="alert-heading">
            Información
          </h4>
          <div class="alert-body pl-8">
            <ul class="infoList">
              <li>Los campos con (*) son obligatorios.</li>
              <li>
                si deseas agregar una suscripción sin limite de tiempo,
                el campo "Hasta" debe estar en blanco.
              </li>
              <li>Si el usuario no cuenta permisos asociados,
              junto con la suscripción obtendrá permisos de lectura.</li>
            </ul>
          </div>
        </b-alert>
        <b-form-group
          label="(*) Usuario"
          label-for="user"
          v-if="!isSelfSubscription"
        >
          <ListUser
            id="user"
            name="user"
            data-cy="user"
            label=""
            placeholder="Seleccione un usuario..."
            v-model="selectedUserId"
            :danger="errors.has('subscription-form.user')"
            :danger-text="errors.first('subscription-form.user')"
            v-validate="'required'"
          />
        </b-form-group>
        <b-form-group
          label="(*) Desde"
          label-for="fromDate"
        >
          <b-form-datepicker
            id="fromDate"
            name="fromDate"
            data-cy="calendar-from-date"
            data-vv-as="Desde"
            placeholder="Selecciona una fecha"
            v-model="fromDate"
            class="form-control bg-white"
            v-validate="'required'"
            locale="es"
            :min="minDate"
            :state="!errors.has('subscription-form.fromDate') &&
            fromDate !== null"
            label-help="Puede usar las flechas del teclado y espacio"
          />
          <FormError
            attributeName="subscription-form.fromDate"
            :errorsForm="errors"
          />
        </b-form-group>
        <b-form-group
          label="Hasta"
          label-for="toDate"
        >
          <b-form-datepicker
            id="toDate"
            name="toDate"
            data-cy="calendar-to-date"
            data-vv-as="Hasta"
            placeholder="Selecciona una fecha"
            v-model="toDate"
            class="form-control bg-white"
            locale="es"
            :min="fromDate || minDate"
            :state="!errors.has('subscription-form.toDate')"
            label-help="Puede usar las flechas del teclado y espacio"
          />
        </b-form-group>
      </div>
      <vs-divider class="pt-32" />
      <div class="d-flex justify-end">
        <vs-button
          class="ml-3"
          v-on:click="save"
          color="primary"
          type="border"
          data-cy="save"
        >
          Suscribir
          <span v-show="loading" class="spinner-border spinner-border-sm text-white" />
        </vs-button>
        <vs-button
          class="ml-3"
          v-on:click="closeModal"
          color="danger"
          type="border"
          @click="clearForm"
        >
          Cancelar
          <span v-show="loading" class="spinner-border spinner-border-sm text-white" />
        </vs-button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import { ioEDFileSubscriptionsAdd, ioEDFoldersSubscriptionsAdd } from '@/febos/servicios/api/repositories.api';
import ListUser from '@/febos/chile/lists/components/ListUser';
import FormError from '@/febos/global/documentManagement/components/FormError';

export default {
  name: 'SubscriptionForm',
  props: {
    isSelfSubscription: {
      type: Boolean,
      required: false,
      default: () => false
    }
  },
  data() {
    return {
      minDate: this.$moment().format('YYYY-MM-DD'),
      loading: false,
      selectedUserId: '',
      fromDate: null,
      toDate: null
    };
  },
  components: {
    ListUser,
    FormError
  },
  methods: {
    ...mapActions('DocManagement', [
      'setSelectedElement',
      'loadFileSubscriptions',
      'loadFolderSubscriptions'
    ]),
    ...mapActions('Modals', [
      'closeModal'
    ]),
    clearForm() {
      this.selectedUserId = '';
      this.fromDate = null;
      this.toDate = null;
      this.$validator.reset();
    },
    async save() {
      if (this.loading) {
        return;
      }
      const formCheck = await this.$validator.validateAll('subscription-form');
      if (formCheck) {
        this.loading = true;
        if (this.getSelectedElement.type === 'file') {
          await ioEDFileSubscriptionsAdd({
            repositoryId: this.getSelectedElement.repositoryId,
            fileId: this.getSelectedElement.id,
            subscriberId: this.isSelfSubscription ? this.currentUser.id : this.selectedUserId,
            fromDate: this.fromDate,
            toDate: this.toDate
          });
          await this.loadFileSubscriptions({
            repositoryId: this.getSelectedElement.repositoryId,
            fileId: this.getSelectedElement.id
          });
        }
        if (this.getSelectedElement.type === 'folder') {
          await ioEDFoldersSubscriptionsAdd({
            repositoryId: this.getSelectedElement.repositoryId,
            folderId: this.getSelectedElement.id,
            subscriberId: this.isSelfSubscription ? this.currentUser.id : this.selectedUserId,
            fromDate: this.fromDate,
            toDate: this.toDate
          });
          await this.loadFolderSubscriptions({
            repositoryId: this.getSelectedElement.repositoryId,
            folderId: this.getSelectedElement.id
          });
        }
        this.$bvToast.toast(
          'La suscripción se ha realizado de forma exitosa',
          {
            title: `Suscripción a ${this.getSelectedElement.name}`,
            variant: 'primary',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 15000
          }
        );
        this.loading = false;
        this.clearForm();
        this.closeModal();
      }
    }
  },
  computed: {
    ...mapGetters('Usuario', [
      'currentUser'
    ]),
    ...mapGetters('DocManagement', [
      'getSelectedElement'
    ]),
    ...mapGetters('Modals', [
      'modalName'
    ]),
    showModal: {
      get() {
        return this.modalName === 'subscriptionFormED';
      },
      set() {
        this.clearForm();
        this.closeModal();
      }
    },
    title() {
      if (this.isSelfSubscription) {
        return `Suscribirme a ${this.getSelectedElement.name}`;
      }
      return `Suscribir usuario a ${this.getSelectedElement.name}`;
    }
  }
};
</script>

<style scoped>
  .infoList{
    list-style-type: circle;
  }
</style>
